import React, { useState, useMemo } from 'react';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from './graphConfig';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import type { CrossFacultyProject, CrossFacultyProjectPerson } from '../../types/api';
import type { TFunction } from 'i18next';

const ROWS_PER_PAGE = 15;

interface CrossFacultyProjectsModalProps {
  facultyA: string | number;
  facultyB: string | number;
  projects: CrossFacultyProject[] | null;
  loading: boolean;
  onClose: () => void;
  t: TFunction;
}

export default function CrossFacultyProjectsModal({
  facultyA,
  facultyB,
  projects,
  loading,
  onClose,
  t,
}: CrossFacultyProjectsModalProps) {
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [page, setPage] = useState<number>(0);
  const [filter, setFilter] = useState<string>('');

  const filtered = useMemo<CrossFacultyProject[]>(() => {
    if (!filter.trim()) return projects || [];
    const q = filter.toLowerCase();
    return (projects || []).filter((p: CrossFacultyProject) =>
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      String(p.year).includes(q) ||
      p.type?.toLowerCase().includes(q) ||
      p.people?.some((pp: CrossFacultyProjectPerson) => pp.name?.toLowerCase().includes(q))
    );
  }, [projects, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const paged = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFilter(e.target.value);
    setPage(0);
    setExpandedRow(null);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[900px] max-h-[80vh] flex flex-col overflow-hidden p-0">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <DialogHeader>
            <DialogTitle>{t('crossFaculty.title')}</DialogTitle>
            <DialogDescription>
              {t('crossFaculty.between')}{' '}
              <strong style={{ color: FACULTY_COLORS[facultyA] || DEFAULT_NODE_COLOR }}>{facultyA}</strong>
              {' '}{t('crossFaculty.and')}{' '}
              <strong style={{ color: FACULTY_COLORS[facultyB] || DEFAULT_NODE_COLOR }}>{facultyB}</strong>
              {!loading && ` (${filtered.length})`}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Filter */}
        {!loading && projects && projects.length > 0 && (
          <div className="px-6 py-3 border-b border-border">
            <Input
              type="text"
              value={filter}
              onChange={handleFilterChange}
              placeholder={t('crossFaculty.filterPlaceholder')}
            />
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="px-6 py-10 text-center text-muted-foreground">
              {t('crossFaculty.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground">
              {filter.trim() ? t('crossFaculty.table.noResults') : t('crossFaculty.noProjects')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wide w-8 pl-4 pr-2"></TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide">{t('crossFaculty.table.name')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide text-center w-[70px]">{t('crossFaculty.table.year')}</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide w-[160px]">{t('crossFaculty.table.people')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((proj: CrossFacultyProject, idx: number) => {
                  const globalIdx = page * ROWS_PER_PAGE + idx;
                  const isExpanded = expandedRow === globalIdx;
                  return (
                    <React.Fragment key={proj.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setExpandedRow(isExpanded ? null : globalIdx)}
                      >
                        <TableCell className="text-xs text-muted-foreground pl-4 pr-2 w-8">
                          <span className={`inline-block transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}>&#9654;</span>
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-foreground">
                          {proj.name}
                          {proj.type && (
                            <Badge variant="info" className="ml-2">{proj.type}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-center font-semibold">
                          {proj.year || '\u2014'}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex gap-1 flex-wrap">
                            {proj.people?.map((pp: CrossFacultyProjectPerson) => (
                              <span
                                key={pp.id}
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ backgroundColor: FACULTY_COLORS[pp.abbreviation] || DEFAULT_NODE_COLOR }}
                                title={`${pp.name} (${pp.abbreviation})`}
                              />
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={4} className="p-0">
                            <div className="py-4 pr-6 pl-12 bg-muted/50">
                              {proj.description && (
                                <div className="text-sm text-muted-foreground mb-3 leading-relaxed">
                                  {proj.description}
                                </div>
                              )}
                              <div className="text-sm">
                                <strong className="text-foreground">{t('crossFaculty.table.people')}:</strong>
                                <div className="mt-1.5 flex flex-col gap-1">
                                  {proj.people?.map((pp: CrossFacultyProjectPerson) => (
                                    <div key={pp.id} className="flex items-center gap-2">
                                      <span
                                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ backgroundColor: FACULTY_COLORS[pp.abbreviation] || DEFAULT_NODE_COLOR }}
                                      />
                                      <span className="text-foreground">{pp.name}</span>
                                      <span className="text-muted-foreground text-xs">
                                        {pp.abbreviation}{pp.department ? ` \u2014 ${pp.department}` : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="px-6 py-3 border-t border-border flex justify-center items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPage((p) => Math.max(0, p - 1)); setExpandedRow(null); }}
              disabled={page === 0}
            >
              &lsaquo;
            </Button>
            <span className="text-sm text-muted-foreground">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); setExpandedRow(null); }}
              disabled={page >= totalPages - 1}
            >
              &rsaquo;
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
